import { useMemo, useState } from 'react';
import { Box, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CustomButton, CustomAutoComplete } from '../../../common/components';
import BeneficiaryDialog from './BeneficiaryDialog';

const getDisplayName = (entity) =>
  entity?.legal_business_name || entity?.company || entity?.cuit || 'Sin nombre';

const normalizeEntities = (entities) => {
  if (!Array.isArray(entities)) return [];
  return entities.filter((entity) => entity && typeof entity === 'object');
};

const BeneficiarySelectWithCreate = ({
  label = 'Beneficiario',
  name = 'uuid_legal_entity',
  value,
  onChange,
  legalEntities = [],
  error,
  helperText,
  disabled = false,
  required = false,
  placeholder = 'Seleccionar',
  selectSx,
  containerSx,
  width = '622px',
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localOptions, setLocalOptions] = useState([]);

  const mergedOptions = useMemo(() => {
    const safeLegalEntities = normalizeEntities(legalEntities);
    const safeLocalOptions = normalizeEntities(localOptions);
    const knownIds = new Set(
      safeLegalEntities
        .map((entity) => entity.uuid)
        .filter(Boolean)
        .map((uuid) => String(uuid))
    );

    const merged = [
      ...safeLegalEntities,
      ...safeLocalOptions.filter((entity) => {
        const uuid = entity.uuid ? String(entity.uuid) : '';
        return uuid && !knownIds.has(uuid);
      }),
    ]
      .filter((entity) => entity.uuid)
      .map((entity) => ({ ...entity, uuid: String(entity.uuid) }));

    return merged;
  }, [legalEntities, localOptions]);

  const handleDialogClose = () => setDialogOpen(false);

  const handleCreated = (beneficiary) => {
    const uuid = beneficiary?.uuid;
    if (uuid !== null && uuid !== undefined && uuid !== '') {
      const normalizedBeneficiary = { ...beneficiary, uuid: String(uuid) };
      setLocalOptions((prev) => [
        normalizedBeneficiary,
        ...prev.filter((entity) => String(entity?.uuid || '') !== normalizedBeneficiary.uuid),
      ]);

      onChange?.({
        target: {
          name,
          value: normalizedBeneficiary.uuid,
        },
      });
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', ...containerSx }}>
        <CustomAutoComplete
          label={label}
          name={name}
          value={value}
          onChange={onChange}
          error={error}
          helperText={helperText}
          disabled={disabled}
          required={required}
          sx={{ flex: 1, maxWidth: width, minWidth: width, ...selectSx }}
        >
          <MenuItem value="">{placeholder}</MenuItem>
          {mergedOptions.map((entity) => (
            <MenuItem key={entity.uuid} value={entity.uuid}>
              {getDisplayName(entity)}
            </MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomButton
          variant="base"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {'>'}
        </CustomButton>
      </Box>

      <BeneficiaryDialog open={dialogOpen} onClose={handleDialogClose} onCreated={handleCreated} />
    </>
  );
};

export default BeneficiarySelectWithCreate;
